import { useState, useEffect } from 'react'
import { FiTrendingUp, FiCheck, FiX, FiClock, FiUser, FiCalendar, FiFilter, FiAlertCircle } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { horasExtrasAPI } from '../services/api'
import { API_URL } from '../config/api'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { PageHeader } from '../ui'
import { formatearFecha } from '../utils/fechas'

const fmtMin = (m) => {
  if (!m) return '0m'
  const h = Math.floor(m / 60), min = m % 60
  if (h > 0 && min > 0) return `${h}h ${min}m`
  return h > 0 ? `${h}h` : `${min}m`
}

const ESTADO_CONFIG = {
  pendiente: { label: 'Pendiente', cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  aprobada:  { label: 'Aprobada',  cls: 'bg-green-50 text-green-700 ring-1 ring-green-200' },
  rechazada: { label: 'Rechazada', cls: 'bg-red-50 text-red-700 ring-1 ring-red-200' },
}

const SolicitudesHorasExtras = ({ embedded = false }) => {
  const { empresaId, esGestion } = useEmpresaGestion()
  const [solicitudes, setSolicitudes] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroEstado, setFiltroEstado] = useState('pendiente')
  const [anio, setAnio] = useState(new Date().getFullYear())
  const [filtroEmpleado, setFiltroEmpleado] = useState('')
  const [empleados, setEmpleados] = useState([])
  const [modal, setModal] = useState(null) // { solicitud, accion: 'aprobar'|'rechazar' }
  const [comentario, setComentario] = useState('')
  const [minutosAprobados, setMinutosAprobados] = useState(0)
  const [guardando, setGuardando] = useState(false)
  // Selección múltiple para acciones bulk (solo en pendientes).
  const [seleccionados, setSeleccionados] = useState([])
  const [modalBulk, setModalBulk] = useState(null) // { accion: 'aprobar'|'rechazar', ids: [] }
  const [comentarioBulk, setComentarioBulk] = useState('')
  const [guardandoBulk, setGuardandoBulk] = useState(false)
  // IDs cuyas peticiones fallaron en el último bulk; se resaltan en la lista.
  const [idsFallidos, setIdsFallidos] = useState(new Set())

  const buildApiUrl = (endpoint) => {
    if (esGestion && empresaId) {
      const sep = endpoint.includes('?') ? '&' : '?'
      return `${endpoint}${sep}empresa_id=${empresaId}`
    }
    return endpoint
  }

  useEffect(() => { cargar(); cargarEmpleados() }, [filtroEstado, anio, filtroEmpleado, empresaId])
  // Reset de la selección si cambian los filtros: los IDs ya no son relevantes.
  useEffect(() => { setSeleccionados([]); setIdsFallidos(new Set()) }, [filtroEstado, anio, filtroEmpleado, empresaId])

  const toggleSel = (id) =>
    setSeleccionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const seleccionables = solicitudes.filter(s => s.estado === 'pendiente')
  const todosSel = seleccionables.length > 0 && seleccionables.every(s => seleccionados.includes(s.id))
  const toggleSelTodos = () => setSeleccionados(todosSel ? [] : seleccionables.map(s => s.id))

  const enviarBulk = async () => {
    if (!modalBulk) return
    setGuardandoBulk(true)
    let ok = 0
    const falladosIds = []
    // Iteración serial: SQLite + horas-extras incluyen disparadores y validaciones,
    // mejor no concurrente. Aplicamos minutos_aprobados = minutos_extra cuando
    // se aprueba (aprobación total). El admin que quiera parcial debe usar el
    // botón individual con su slider.
    for (const id of modalBulk.ids) {
      try {
        const sol = solicitudes.find(s => s.id === id)
        if (!sol) { falladosIds.push(id); continue }
        await horasExtrasAPI.responder(id, {
          estado: modalBulk.accion === 'aprobar' ? 'aprobada' : 'rechazada',
          comentario_admin: comentarioBulk.trim() || null,
          ...(modalBulk.accion === 'aprobar' && { minutos_aprobados: sol.minutos_extra }),
        })
        ok++
      } catch { falladosIds.push(id) }
    }
    const ko = falladosIds.length
    if (ok > 0 && ko === 0) toast.success(`${ok} solicitud${ok > 1 ? 'es' : ''} procesada${ok > 1 ? 's' : ''}`)
    else if (ok > 0 && ko > 0) toast(`${ok} OK · ${ko} con error. Revisa las marcadas en rojo.`, { icon: '⚠️', duration: 6000 })
    else toast.error(`No se pudo procesar`)
    setModalBulk(null)
    setComentarioBulk('')
    setGuardandoBulk(false)
    // Mantenemos los fallidos seleccionados para que el admin pueda reintentar.
    setSeleccionados(falladosIds)
    setIdsFallidos(new Set(falladosIds))
    cargar()
  }

  const cargarEmpleados = async () => {
    try {
      const token = localStorage.getItem('token')
      const url = esGestion && empresaId ? `${API_URL}/empleados?empresa_id=${empresaId}` : `${API_URL}/empleados`
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      if (res.ok) { const d = await res.json(); setEmpleados(d.empleados || []) }
    } catch {}
  }

  const cargar = async () => {
    setLoading(true)
    try {
      const params = { estado: filtroEstado, anio }
      if (filtroEmpleado) params.empleado_id = filtroEmpleado
      const res = await horasExtrasAPI.todas(params)
      setSolicitudes(res.data || [])
    } catch {
      toast.error('Error al cargar solicitudes')
    } finally {
      setLoading(false)
    }
  }

  const responder = async () => {
    if (!modal) return
    setGuardando(true)
    try {
      await horasExtrasAPI.responder(modal.solicitud.id, {
        estado: modal.accion === 'aprobar' ? 'aprobada' : 'rechazada',
        comentario_admin: comentario.trim() || null,
        ...(modal.accion === 'aprobar' && { minutos_aprobados: minutosAprobados })
      })
      toast.success(modal.accion === 'aprobar' ? 'Hora extra aprobada' : 'Solicitud rechazada')
      setModal(null)
      setComentario('')
      cargar()
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al procesar')
    } finally {
      setGuardando(false)
    }
  }

  const pendientes = solicitudes.filter(s => s.estado === 'pendiente').length

  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']
  const anios = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i)

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      {!embedded && (
        <PageHeader
          num="§ HE"
          kicker="Horas extras"
          title="Solicitudes"
          italic="pendientes."
          sub={
            pendientes > 0
              ? `${pendientes} ${pendientes === 1 ? 'solicitud pendiente' : 'solicitudes pendientes'} de revisión.`
              : 'Gestiona las solicitudes de horas extra de tus empleados.'
          }
          className="!px-0 !pt-0 !pb-5 !border-0"
        />
      )}

      {/* Filtros */}
      <div className="ui-card p-5 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          {/* Estado — mismo patrón visual que el toggle de Permisos en
              GestionFichajes (rounded-full + borde + texto azul al activar). */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-[var(--ink-3)]">Mostrar:</span>
            {[
              { value: 'pendiente', label: 'Pendientes' },
              { value: 'aprobada',  label: 'Aprobadas' },
              { value: 'rechazada', label: 'Rechazadas' },
              { value: 'todas',     label: 'Todas' },
            ].map(opt => {
              const activo = filtroEstado === opt.value
              return (
                <button
                  key={opt.value}
                  onClick={() => setFiltroEstado(opt.value)}
                  className={`px-3 py-1 text-[11px] rounded-full border transition inline-flex items-center gap-1.5 ${
                    activo
                      ? 'bg-[rgba(37,99,235,.06)] border-[var(--brand-1)] text-[var(--brand-1)]'
                      : 'bg-[var(--bg-2)] border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--line)]'
                  }`}
                >
                  {opt.label}
                  {opt.value === 'pendiente' && pendientes > 0 && (
                    <span className="bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded-full leading-none">
                      {pendientes}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div className="h-5 w-px bg-[var(--line)]" />

          {/* Empleado */}
          <select value={filtroEmpleado} onChange={e => setFiltroEmpleado(e.target.value)}
            className="px-3 py-1.5 text-sm border border-[var(--line-2)] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Todos los empleados</option>
            {empleados.filter(e => e.activo).map(e => (
              <option key={e.id} value={e.id}>{e.nombre}</option>
            ))}
          </select>

          {/* Año */}
          <select value={anio} onChange={e => setAnio(parseInt(e.target.value))}
            className="px-3 py-1.5 text-sm border border-[var(--line-2)] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="ui-card p-5 divide-y divide-gray-50">
          {[1,2,3].map(i => (
            <div key={i} className="flex items-center gap-4 px-5 py-4 animate-pulse">
              <div className="w-10 h-10 rounded-xl bg-[var(--bg-2)]" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 bg-[var(--bg-2)] rounded-full w-32" />
                <div className="h-2.5 bg-[var(--bg-2)] rounded-full w-48" />
              </div>
              <div className="h-8 w-20 bg-[var(--bg-2)] rounded-lg" />
            </div>
          ))}
        </div>
      ) : solicitudes.length === 0 ? (
        <div className="ui-card p-5 flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
            <FiTrendingUp className="h-7 w-7 text-indigo-400" />
          </div>
          <p className="font-semibold text-[var(--ink)] mb-1">Sin solicitudes</p>
          <p className="text-sm text-[var(--ink-4)]">
            {filtroEstado === 'pendiente'
              ? 'No hay solicitudes pendientes de revisión'
              : `No hay solicitudes con estado "${filtroEstado}" en este período`}
          </p>
        </div>
      ) : (
        <>
          {/* Toolbar selección múltiple — solo en pendientes con más de 1 item */}
          {filtroEstado === 'pendiente' && seleccionables.length > 1 && (
            <div className="space-y-2">
              <label className="inline-flex items-center gap-1.5 text-[12px] text-[var(--ink-3)] cursor-pointer hover:text-[var(--ink-2)]">
                <input
                  type="checkbox"
                  checked={todosSel}
                  onChange={toggleSelTodos}
                  className="w-3.5 h-3.5 rounded border-[var(--line)] focus:ring-[var(--brand-1)]"
                />
                Seleccionar todos
              </label>
              {seleccionados.length > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-[rgba(37,99,235,.04)] border border-[rgba(37,99,235,.15)]">
                  <span className="text-[13px] font-medium text-[var(--brand-1)]">
                    {seleccionados.length} seleccionada{seleccionados.length > 1 ? 's' : ''}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSeleccionados([])}
                      className="px-3 py-1.5 text-xs font-semibold text-[var(--ink-2)] bg-[var(--bg-2)] hover:bg-[var(--line)] rounded-lg"
                    >
                      Deseleccionar
                    </button>
                    <button
                      onClick={() => { setModalBulk({ accion: 'rechazar', ids: seleccionados }); setComentarioBulk('') }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-[var(--ink-2)] hover:text-red-600 bg-[var(--bg-2)] hover:bg-red-50 rounded-lg"
                    >
                      <FiX className="h-3.5 w-3.5" /> Rechazar ({seleccionados.length})
                    </button>
                    <button
                      onClick={() => { setModalBulk({ accion: 'aprobar', ids: seleccionados }); setComentarioBulk('') }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg"
                    >
                      <FiCheck className="h-3.5 w-3.5" /> Aprobar ({seleccionados.length})
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        <div className="ui-card p-5 overflow-hidden">
          {/* Header tabla */}
          <div className="hidden md:grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 px-5 py-2.5 bg-[var(--bg-2)]/80 border-b border-[var(--line)]">
            {['Empleado', 'Fecha', 'Tiempo extra', 'Estado', ''].map((h, i) => (
              <span key={i} className="text-xs font-semibold text-[var(--ink-4)] uppercase tracking-wider">{h}</span>
            ))}
          </div>

          <div className="divide-y divide-gray-50">
            {solicitudes.map(s => {
              const estadoConf = ESTADO_CONFIG[s.estado] || ESTADO_CONFIG.pendiente
              return (
                <div
                  key={s.id}
                  className={`flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 hover:bg-[var(--bg-2)]/60 transition-colors ${
                    idsFallidos.has(s.id) ? 'bg-red-50/40 ring-1 ring-red-200' : ''
                  }`}
                >

                  {s.estado === 'pendiente' && (
                    <input
                      type="checkbox"
                      checked={seleccionados.includes(s.id)}
                      onChange={() => toggleSel(s.id)}
                      className="w-4 h-4 rounded border-[var(--line)] focus:ring-[var(--brand-1)] flex-shrink-0"
                    />
                  )}

                  {idsFallidos.has(s.id) && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] font-semibold rounded-full bg-red-50 text-red-700 ring-1 ring-red-200"
                      title="Hubo un error al procesar esta solicitud en la última operación. Si persiste tras reintentar, consulta con soporte."
                    >
                      <FiAlertCircle className="h-3 w-3" /> Error · soporte
                    </span>
                  )}

                  {/* Empleado */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {s.empleado_nombre?.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-[var(--ink)] truncate">{s.empleado_nombre}</p>
                      {s.motivo && (
                        <p className="text-xs text-[var(--ink-4)] truncate italic">"{s.motivo}"</p>
                      )}
                    </div>
                  </div>

                  {/* Fecha */}
                  <div className="flex items-center gap-1.5 text-sm text-[var(--ink-2)] w-28 flex-shrink-0">
                    <FiCalendar className="h-3.5 w-3.5 text-[var(--ink-4)]" />
                    {formatearFecha(s.fecha)}
                  </div>

                  {/* Tiempo extra */}
                  <div className="flex items-center gap-1.5 w-28 flex-shrink-0">
                    <FiTrendingUp className="h-3.5 w-3.5 text-indigo-500" />
                    {s.estado === 'aprobada' && s.minutos_aprobados && s.minutos_aprobados < s.minutos_extra ? (
                      <span className="text-sm">
                        <span className="font-semibold text-green-700">{fmtMin(s.minutos_aprobados)}</span>
                        <span className="text-[var(--ink-4)] text-xs"> / {fmtMin(s.minutos_extra)}</span>
                      </span>
                    ) : (
                      <span className="text-sm font-semibold text-indigo-700">+{fmtMin(s.minutos_extra)}</span>
                    )}
                  </div>

                  {/* Estado */}
                  <span className={`px-2.5 py-1 text-xs font-semibold rounded-lg w-24 text-center flex-shrink-0 ${estadoConf.cls}`}>
                    {estadoConf.label}
                  </span>

                  {/* Acciones */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {s.estado === 'pendiente' ? (
                      <>
                        <button
                          onClick={() => { setModal({ solicitud: s, accion: 'aprobar' }); setComentario(''); setMinutosAprobados(s.minutos_extra) }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-colors"
                        >
                          <FiCheck className="h-3.5 w-3.5" /> Aprobar
                        </button>
                        <button
                          onClick={() => { setModal({ solicitud: s, accion: 'rechazar' }); setComentario('') }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-[var(--bg-2)] hover:bg-red-50 text-[var(--ink-2)] hover:text-red-600 text-xs font-semibold rounded-lg transition-colors"
                        >
                          <FiX className="h-3.5 w-3.5" /> Rechazar
                        </button>
                      </>
                    ) : s.comentario_admin ? (
                      <span className="text-xs text-[var(--ink-4)] italic max-w-[160px] truncate" title={s.comentario_admin}>
                        {s.comentario_admin}
                      </span>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        </>
      )}

      {/* Modal bulk: aprobar / rechazar varias a la vez */}
      {modalBulk && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => !guardandoBulk && setModalBulk(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-4 mb-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                modalBulk.accion === 'aprobar' ? 'bg-green-50' : 'bg-red-50'
              }`}>
                {modalBulk.accion === 'aprobar'
                  ? <FiCheck className="h-5 w-5 text-green-600" />
                  : <FiX className="h-5 w-5 text-red-500" />
                }
              </div>
              <div>
                <h3 className="text-base font-bold text-[var(--ink)]">
                  {modalBulk.accion === 'aprobar'
                    ? `Aprobar ${modalBulk.ids.length} solicitud${modalBulk.ids.length > 1 ? 'es' : ''}`
                    : `Rechazar ${modalBulk.ids.length} solicitud${modalBulk.ids.length > 1 ? 'es' : ''}`}
                </h3>
                <p className="text-sm text-[var(--ink-3)] mt-0.5">
                  {modalBulk.accion === 'aprobar'
                    ? 'Se aprobará la cantidad solicitada de cada una. Para aprobaciones parciales, usa el botón individual.'
                    : 'El motivo se aplicará a todas.'}
                </p>
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">
                Comentario {modalBulk.accion === 'rechazar' ? <span className="text-red-500">*</span> : <span className="text-[var(--ink-4)] font-normal">(opcional)</span>}
              </label>
              <textarea
                value={comentarioBulk}
                onChange={e => setComentarioBulk(e.target.value)}
                placeholder={modalBulk.accion === 'rechazar' ? 'Motivo del rechazo...' : 'Comentario opcional...'}
                className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none h-20"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setModalBulk(null)}
                disabled={guardandoBulk}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-[var(--ink-2)] bg-[var(--bg-2)] hover:bg-[var(--line)] rounded-xl"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (modalBulk.accion === 'rechazar' && !comentarioBulk.trim()) {
                    toast.error('Indica el motivo del rechazo')
                    return
                  }
                  enviarBulk()
                }}
                disabled={guardandoBulk}
                className={`flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-xl flex items-center justify-center gap-2 ${
                  modalBulk.accion === 'aprobar' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {guardandoBulk
                  ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : (modalBulk.accion === 'aprobar' ? 'Aprobar todas' : 'Rechazar todas')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal responder */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-4 mb-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                modal.accion === 'aprobar' ? 'bg-green-50' : 'bg-red-50'
              }`}>
                {modal.accion === 'aprobar'
                  ? <FiCheck className="h-5 w-5 text-green-600" />
                  : <FiX className="h-5 w-5 text-red-500" />
                }
              </div>
              <div>
                <h3 className="text-base font-bold text-[var(--ink)]">
                  {modal.accion === 'aprobar' ? 'Aprobar hora extra' : 'Rechazar hora extra'}
                </h3>
                <p className="text-sm text-[var(--ink-3)] mt-0.5">
                  <strong>{modal.solicitud.empleado_nombre}</strong> · {formatearFecha(modal.solicitud.fecha)}
                </p>
              </div>
            </div>

            {/* Resumen de la solicitud */}
            <div className="bg-[var(--bg-2)] rounded-xl p-3 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-[var(--ink-3)]">Solicitado</span>
                <span className="font-semibold text-indigo-700">+{fmtMin(modal.solicitud.minutos_extra)}</span>
              </div>
              {modal.solicitud.minutos_turno > 0 && (
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-[var(--ink-3)]">Jornada prevista</span>
                  <span className="text-[var(--ink-2)]">{fmtMin(modal.solicitud.minutos_turno)}</span>
                </div>
              )}
            </div>

            {/* Selector de tiempo aprobado (solo al aprobar) */}
            {modal.accion === 'aprobar' && (
              <div className="mb-4">
                <label className="text-xs font-medium text-[var(--ink-2)] mb-2 block">Tiempo extra aprobado</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="1"
                    max={modal.solicitud.minutos_extra}
                    value={minutosAprobados}
                    onChange={e => setMinutosAprobados(parseInt(e.target.value))}
                    className="flex-1 h-2 bg-[var(--line)] rounded-lg appearance-none cursor-pointer accent-green-600"
                  />
                  <span className="text-lg font-bold text-green-700 min-w-[70px] text-right">{fmtMin(minutosAprobados)}</span>
                </div>
                <div className="flex justify-between text-[11px] text-[var(--ink-4)] mt-1">
                  <span>1m</span>
                  <span>{fmtMin(modal.solicitud.minutos_extra)} (total)</span>
                </div>
                {minutosAprobados < modal.solicitud.minutos_extra && (
                  <p className="text-xs text-amber-600 mt-2 bg-amber-50 rounded-lg px-3 py-1.5">
                    Aprobación parcial: {fmtMin(minutosAprobados)} de {fmtMin(modal.solicitud.minutos_extra)} solicitados
                  </p>
                )}
              </div>
            )}

            <div className="mb-4">
              <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Comentario <span className="text-[var(--ink-4)] font-normal">(opcional)</span></label>
              <textarea
                value={comentario}
                onChange={e => setComentario(e.target.value)}
                placeholder={modal.accion === 'rechazar' ? 'Motivo del rechazo...' : 'Comentario opcional...'}
                className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none h-16"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setModal(null)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-[var(--ink-2)] bg-[var(--bg-2)] hover:bg-[var(--line)] rounded-xl transition-colors"
                disabled={guardando}
              >
                Cancelar
              </button>
              <button
                onClick={responder}
                disabled={guardando}
                className={`flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-colors flex items-center justify-center gap-2 ${
                  modal.accion === 'aprobar'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {guardando ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : modal.accion === 'aprobar' ? (
                  minutosAprobados < modal.solicitud.minutos_extra ? 'Aprobar parcialmente' : 'Aprobar todo'
                ) : 'Confirmar rechazo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SolicitudesHorasExtras
